import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../CartContext";
import { useAuth } from "../AuthContext";
import { api, fmtINR, errMsg } from "../api";
import { CheckCircle, ShieldCheck, AlertTriangle } from "lucide-react";

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

function loadGoKwikScript() {
    return new Promise((resolve) => {
        const src = "https://pdp.gokwik.co/v4/build/gokwik.js";
        if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.body.appendChild(s);
    });
}

/* Adapter boundary — GoKwik's exact web SDK contract is supplied at merchant onboarding.
 * We load the official SDK and probe the documented init/open surface; if the contract
 * differs for this merchant account, we return false and the caller falls back to Razorpay.
 * NEVER pass the App Secret here — the browser only ever sees the public App ID. */
async function launchGoKwik(order, settings, form, cb) {
    if (!settings.gokwik_enabled || !settings.gokwik_app_id) return false;
    const ok = await loadGoKwikScript();
    if (!ok) return false;
    const gk = window.gokwikSdk || window.GoKwik || window.gwkw;
    const init = gk?.init || gk?.initCheckout;
    const open = gk?.open || gk?.startCheckout;
    if (typeof init !== "function" || typeof open !== "function") return false;
    try {
        init.call(gk, { appId: settings.gokwik_app_id, environment: "production" });
        open.call(gk, {
            merchantOrderId: order.id,
            orderNumber: order.order_number,
            amount: order.total,
            currency: "INR",
            customer: { name: form.name, email: form.email, phone: form.phone },
            onSuccess: cb.onSuccess,
            onFailure: cb.onFailure,
            onClose: cb.onClose,
        });
        return true;
    } catch (e) {
        return false;
    }
}

export default function Checkout() {
    const { items, subtotal, clear } = useCart();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    // Orders require a signed-in customer — send guests to login and bring them back.
    useEffect(() => {
        if (!authLoading && !user) {
            navigate("/login", { replace: true, state: { next: "/checkout" } });
        }
    }, [authLoading, user, navigate]);
    const [placing, setPlacing] = useState(false);
    const [orderResult, setOrderResult] = useState(null);
    const [error, setError] = useState("");
    const [storeSettings, setStoreSettings] = useState({});
    const isVip = !!user?.is_vip;
    const [paymentMethod, setPaymentMethod] = useState(isVip ? "vip" : "razorpay");
    const couponCode = localStorage.getItem("uce_coupon") || "";

    useEffect(() => {
        api.get("/settings").then((r) => setStoreSettings(r.data || {})).catch(() => {});
    }, []);

    useEffect(() => {
        setPaymentMethod(isVip ? "vip" : "razorpay");
    }, [isVip]);

    const [form, setForm] = useState({
        name: user?.name || "", phone: user?.phone || "", email: user?.email || "",
        line1: "", line2: "", city: "", state: "", pincode: "", country: "India",
    });

    useEffect(() => {
        // Pre-load Razorpay script so the popup opens instantly on submit.
        // loadRazorpayScript is module-scoped and stable; safe to omit from deps.
        if (!isVip) loadRazorpayScript();
    }, [isVip]);

    const ship = isVip || subtotal === 0 || subtotal >= 499 ? 0 : 49;
    const codFee = (!isVip && paymentMethod === "cod") ? 49 : 0;
    const totalPreview = isVip ? 0 : subtotal + ship + codFee;
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const place = async (e) => {
        e.preventDefault();
        if (items.length === 0) return;
        setPlacing(true);
        setError("");
        try {
            // 1. Create the order
            const { data: order } = await api.post("/checkout", {
                items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
                address: {
                    name: form.name, phone: form.phone, line1: form.line1, line2: form.line2,
                    city: form.city, state: form.state, pincode: form.pincode, country: form.country,
                },
                coupon_code: couponCode || null,
                payment_method: isVip ? "razorpay" : paymentMethod,
                notes: "",
            });

            if (isVip || order.is_vip_order) {
                setOrderResult(order);
                clear();
                localStorage.removeItem("uce_coupon");
                return;
            }

            if (paymentMethod === "cod") {
                setOrderResult(order);
                clear();
                localStorage.removeItem("uce_coupon");
                return;
            }

            if (paymentMethod === "gokwik") {
                // Live GoKwik launch — order is already created server-side (totals verified).
                // Payment confirmation is authoritative only via the signed backend webhook
                // (POST /api/gokwik/webhook); the onSuccess callback is UX-only.
                const launched = await launchGoKwik(order, storeSettings, form, {
                    onSuccess: () => {
                        setOrderResult({ ...order, payment_status: "paid" });
                        clear();
                        localStorage.removeItem("uce_coupon");
                    },
                    onFailure: (e) => setError(e?.message || "GoKwik checkout failed. Please retry or use Razorpay."),
                    onClose: () => setPlacing(false),
                });
                if (launched) return;
                setError("GoKwik couldn't start on this device — completing your payment securely via Razorpay instead.");
                // Continue to Razorpay flow below
            }

            // 2. Razorpay flow — create gateway order
            let rzp;
            try {
                const r = await api.post("/razorpay/create-order", { order_id: order.id });
                rzp = r.data;
            } catch (err) {
                // Gateway not configured. Order is created with payment_status='pending' — show partial success.
                setError(errMsg(err) + " Your order was placed but payment couldn't be initiated. We'll contact you.");
                setOrderResult({ ...order, payment_status: "pending_gateway" });
                clear();
                localStorage.removeItem("uce_coupon");
                return;
            }

            const ok = await loadRazorpayScript();
            if (!ok) {
                setError("Could not load Razorpay. Please try again or use COD.");
                return;
            }

            await new Promise((resolve) => {
                const rz = new window.Razorpay({
                    key: rzp.key_id,
                    amount: rzp.amount,
                    currency: rzp.currency,
                    name: rzp.business_name || "UCare Essentials",
                    description: `Order ${rzp.order_number}`,
                    image: rzp.logo_url || undefined,
                    order_id: rzp.razorpay_order_id,
                    prefill: { name: form.name, email: form.email, contact: form.phone },
                    notes: { internal_order_id: order.id },
                    theme: { color: "#8a7a6e" },
                    handler: async (resp) => {
                        try {
                            await api.post("/razorpay/verify", {
                                order_id: order.id,
                                razorpay_order_id: resp.razorpay_order_id,
                                razorpay_payment_id: resp.razorpay_payment_id,
                                razorpay_signature: resp.razorpay_signature,
                            });
                            setOrderResult({ ...order, payment_status: "paid", razorpay_payment_id: resp.razorpay_payment_id });
                            clear();
                            localStorage.removeItem("uce_coupon");
                        } catch (e) {
                            setError("Payment captured but verification failed. Contact support with order " + order.order_number);
                        } finally {
                            resolve();
                        }
                    },
                    modal: {
                        ondismiss: () => {
                            setError("Payment was cancelled. Your order is saved as pending — retry from My Orders.");
                            setOrderResult({ ...order, payment_status: "cancelled" });
                            clear();
                            localStorage.removeItem("uce_coupon");
                            resolve();
                        },
                    },
                });
                rz.open();
            });
        } catch (err) {
            setError(errMsg(err));
        } finally {
            setPlacing(false);
        }
    };

    if (orderResult) {
        const paid = orderResult.payment_status === "paid";
        return (
            <div className="max-w-2xl mx-auto px-5 py-20 text-center" data-testid="order-success">
                {paid ? (
                    <CheckCircle className="w-16 h-16 text-green-700 mx-auto mb-4" />
                ) : (
                    <AlertTriangle className="w-16 h-16 text-[var(--gold)] mx-auto mb-4" />
                )}
                <h1 className="font-serif text-4xl mb-2 text-[var(--ink)]">
                    {paid ? "Payment Confirmed!" : "Order Saved"}
                </h1>
                <p className="text-[var(--body)] mb-6">
                    Order <span className="font-mono text-[var(--ink)]">{orderResult.order_number}</span>{" "}
                    {paid ? "has been paid and is being prepared." : "is awaiting payment / confirmation."}
                </p>
                <div className="card p-6 text-left mb-6">
                    <Row k="Subtotal" v={fmtINR(orderResult.subtotal)} />
                    <Row k="Shipping" v={orderResult.shipping === 0 ? "Free" : fmtINR(orderResult.shipping)} />
                    {orderResult.discount > 0 && <Row k={`Discount (${orderResult.coupon_code})`} v={`−${fmtINR(orderResult.discount)}`} />}
                    <div className="h-px bg-[var(--line)] my-2" />
                    <Row k="Total" v={fmtINR(orderResult.total)} bold />
                    <div className="text-[12px] text-[var(--body)] mt-3">
                        Payment: {{ cod: "Cash on Delivery", gokwik: "GoKwik 1-Click", vip_free: "VIP Complimentary" }[orderResult.payment_method] || "Razorpay"} ·{" "}
                        Status: <span className="font-medium">{orderResult.payment_status}</span>
                    </div>
                </div>
                <div className="flex gap-3 justify-center">
                    <Link to="/shop" className="btn btn-outline btn-sm">Continue Shopping</Link>
                    {user && <Link to="/account" className="btn btn-primary btn-sm">View My Orders</Link>}
                </div>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="empty-checkout">
                <div className="text-5xl mb-3">🌸</div>
                <h1 className="font-serif text-3xl text-[var(--ink)] mb-2">Your cart is empty</h1>
                <Link to="/shop" className="btn btn-primary mt-4">Browse products</Link>
            </div>
        );
    }

    return (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-8 lg:py-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10" data-testid="checkout-page">
            <form onSubmit={place} className="lg:col-span-7 space-y-8 min-w-0">
                <div>
                    <p className="overline mb-3">Step 01</p>
                    <h2 className="font-serif text-2xl sm:text-3xl text-[var(--ink)] mb-5">Contact & Shipping</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Full name" required value={form.name} onChange={set("name")} testid="ck-name" />
                        <Field label="Phone" required value={form.phone} onChange={set("phone")} testid="ck-phone" />
                        <Field label="Email" type="email" required value={form.email} onChange={set("email")} testid="ck-email" wide />
                        <Field label="Address line 1" required value={form.line1} onChange={set("line1")} testid="ck-line1" wide />
                        <Field label="Address line 2" value={form.line2} onChange={set("line2")} wide />
                        <Field label="City" required value={form.city} onChange={set("city")} testid="ck-city" />
                        <Field label="State" required value={form.state} onChange={set("state")} testid="ck-state" />
                        <Field label="Pincode" required value={form.pincode} onChange={set("pincode")} testid="ck-pin" />
                        <Field label="Country" value={form.country} onChange={set("country")} />
                    </div>
                </div>

                <div>
                    <p className="overline mb-3">Step 02</p>
                    <h2 className="font-serif text-2xl sm:text-3xl text-[var(--ink)] mb-5">Payment</h2>
                    {isVip ? (
                        <div className="card p-5 border-[var(--taupe)] bg-[var(--bg-alt)]/40" data-testid="vip-checkout-banner">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-[var(--taupe)] text-white grid place-items-center shrink-0 text-lg">★</div>
                                <div>
                                    <div className="font-serif text-[20px] text-[var(--ink)]">VIP Complimentary Order</div>
                                    <p className="text-[13px] text-[var(--body)] mt-1">
                                        Your VIP membership covers this order in full. No payment required — we'll begin processing immediately.
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3">
                                <PayOption
                                    id="razorpay"
                                    checked={paymentMethod === "razorpay"}
                                    onChange={() => setPaymentMethod("razorpay")}
                                    title="Razorpay"
                                    sub="UPI · Cards · NetBanking · Wallets"
                                    badge="Recommended"
                                />
                                {storeSettings.gokwik_enabled && (
                                    <PayOption
                                        id="gokwik"
                                        checked={paymentMethod === "gokwik"}
                                        onChange={() => setPaymentMethod("gokwik")}
                                        title="GoKwik 1-Click"
                                        sub="Faster checkout with prefilled address & UPI"
                                        badge="Fastest"
                                    />
                                )}
                                <PayOption
                                    id="cod"
                                    checked={paymentMethod === "cod"}
                                    onChange={() => setPaymentMethod("cod")}
                                    title="Cash on Delivery"
                                    sub="Pay when you receive · ₹49 COD fee applies"
                                />
                            </div>
                            {paymentMethod === "razorpay" && (
                                <div className="mt-3 text-[12px] text-[var(--body)] flex items-center gap-1.5">
                                    <ShieldCheck size={14} /> Secured by Razorpay's PCI-compliant gateway.
                                </div>
                            )}
                        </>
                    )}
                </div>

                {error && <div className="text-[var(--terra)] text-sm" data-testid="checkout-error">{error}</div>}

                <button
                    className="btn btn-primary w-full"
                    disabled={placing}
                    data-testid="place-order-btn"
                >
                    {placing ? "Processing…" : isVip ? "Confirm VIP Order · Free" : `${paymentMethod === "razorpay" ? "Pay" : "Place Order"} · ${fmtINR(totalPreview)}`}
                </button>
            </form>

            <aside className="lg:col-span-5 lg:sticky lg:top-24 self-start card p-5 sm:p-6 min-w-0">
                <h3 className="font-serif text-xl sm:text-2xl text-[var(--ink)] mb-4">Order Summary</h3>
                <ul className="divide-y divide-[var(--line-soft)]">
                    {items.map((i) => (
                        <li key={i.product_id} className="py-3 flex gap-3 items-center">
                            <div className="w-12 h-12 rounded-lg bg-[var(--bg-alt)] grid place-items-center text-2xl overflow-hidden shrink-0">
                                {i.image ? <img src={i.image} alt="" className="w-full h-full rounded-lg object-cover" /> : i.emoji}
                            </div>
                            <div className="flex-1 min-w-0 text-[13px]">
                                <div className="font-medium text-[var(--ink)] truncate">{i.name}</div>
                                <div className="text-[var(--body)] text-[12px]">Qty {i.quantity}</div>
                            </div>
                            <div className="font-serif text-[15px] text-[var(--ink)]">{fmtINR(i.price * i.quantity)}</div>
                        </li>
                    ))}
                </ul>
                <div className="mt-4 space-y-1.5 text-[14px]">
                    <Row k="Subtotal" v={fmtINR(subtotal)} />
                    {isVip ? (
                        <>
                            <Row k="VIP complimentary" v={`−${fmtINR(subtotal)}`} accent />
                            <div className="h-px bg-[var(--line)]" />
                            <Row k="Total" v={fmtINR(0)} bold />
                        </>
                    ) : (
                        <>
                            <Row k="Shipping" v={ship === 0 ? "Free" : fmtINR(ship)} />
                            {paymentMethod === "cod" && <Row k="COD Fee" v={fmtINR(codFee)} />}
                            {couponCode && <Row k={`Coupon ${couponCode}`} v="Applied at checkout" accent />}
                            <div className="h-px bg-[var(--line)]" />
                            <Row k="Total (preview)" v={fmtINR(totalPreview)} bold />
                        </>
                    )}
                </div>
            </aside>
        </div>
    );
}

function Field({ label, type = "text", required, value, onChange, wide, testid }) {
    return (
        <div className={wide ? "sm:col-span-2" : ""}>
            <label className="lbl">{label}{required && " *"}</label>
            <input className="field" type={type} required={required} value={value} onChange={onChange} data-testid={testid} />
        </div>
    );
}

function PayOption({ id, checked, onChange, title, sub, badge }) {
    return (
        <label
            className={`flex items-start gap-3 card p-4 cursor-pointer ${checked ? "border-[var(--taupe)] bg-[var(--bg-alt)]/40" : ""}`}
            data-testid={`pay-${id}`}
        >
            <input type="radio" name="payment" checked={checked} onChange={onChange} className="mt-1" />
            <div className="flex-1">
                <div className="flex items-center gap-2">
                    <div className="font-medium text-[var(--ink)]">{title}</div>
                    {badge && <span className="text-[10px] uppercase tracking-[0.16em] bg-[var(--taupe)] text-white px-2 py-0.5 rounded-full">{badge}</span>}
                </div>
                <div className="text-[12px] text-[var(--body)]">{sub}</div>
            </div>
        </label>
    );
}

function Row({ k, v, accent, bold }) {
    return (
        <div className="flex justify-between">
            <span className={bold ? "font-medium text-[var(--ink)]" : "text-[var(--body)]"}>{k}</span>
            <span className={`${bold ? "font-serif text-[16px] text-[var(--ink)]" : "text-[var(--ink-soft)]"} ${accent ? "!text-[var(--terra)]" : ""}`}>{v}</span>
        </div>
    );
}
